import React from "react";
import { trackEvent } from "@/lib/analytics";
import { Link } from "react-router-dom";
import { Sparkles, ImageIcon } from "lucide-react";
import { TrustBadge } from "@/components/TrustBadge";
import { CategoryTag } from "@/components/CategoryTag";
import { ShareButton } from "@/components/ShareCard";
import { useI18n, formatRelative, formatExact, itemDate } from "@/i18n";

export function ArticleCard({ article, index = 0 }) {
  const { t, lang, pick, title } = useI18n();
  const cat = article.categories?.[0];
  const summary = pick(article, "summary");
  const minimal = !article.title;

  return (
    <article
      data-testid="article-card"
      data-display-mode={article.display_mode}
      className="group fade-up bg-white rounded-xl border border-slate-200/80 overflow-hidden hover:border-violet-300 hover:shadow-[0_8px_30px_rgba(147,51,234,0.08)] transition-all duration-200 flex flex-col"
      style={{ animationDelay: `${Math.min(index * 40, 300)}ms` }}
    >
      {article.image_url ? (
        <Link to={`/article/${article.id}`} className="block relative aspect-[16/9] overflow-hidden bg-slate-100">
          <img src={article.image_url} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <span className="absolute bottom-2 start-2 inline-flex items-center gap-1 bg-black/55 text-white text-[9px] font-mono px-1.5 py-0.5 rounded">
            <ImageIcon size={9} /> {t("image.illustration")}
          </span>
        </Link>
      ) : (
        <Link to={`/article/${article.id}`} className="block aspect-[16/9] bg-gradient-to-br from-violet-100 to-slate-100 flex items-center justify-center">
          <span className="font-display font-bold text-violet-300 text-lg">NxtTechNews</span>
        </Link>
      )}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-2 mb-2.5">
          <CategoryTag slug={cat || "ai-companies"} />
          <TrustBadge status={article.status} mode={article.display_mode} />
        </div>

        <Link to={`/article/${article.id}`}>
          <h3 data-testid="article-card-title" className={`font-display font-semibold text-[17px] leading-snug tracking-tight group-hover:text-violet-800 transition-colors line-clamp-3 ${minimal ? "text-slate-700" : "text-slate-900"}`}>
            {title(article)}
          </h3>
        </Link>

        {summary && (
          <div className="mt-3">
            <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-violet-600 mb-1">
              <Sparkles size={10} /> <span data-testid="article-ai-summary-badge">{t("common.aiSummary")}</span>
            </span>
            <p className="text-[13.5px] text-slate-600 leading-relaxed line-clamp-3">{summary}</p>
          </div>
        )}

        <div className="mt-auto pt-4 flex items-center justify-between gap-2 text-xs">
          <div className="min-w-0">
            <span className="font-medium text-slate-700 truncate block">{article.source_name}</span>
            <span className="text-muted-foreground" title={formatExact(itemDate(article), lang)}>
              {!article.published_at && `${t("common.discovered")} · `}{formatRelative(itemDate(article), lang)}
            </span>
          </div>
          <div className="shrink-0 flex items-center gap-1">
            <ShareButton article={article} />
            <a href={article.original_url} target="_blank" rel="noopener noreferrer" data-testid="btn-read-original" className="text-violet-700 font-medium hover:underline" onClick={(e) => { e.stopPropagation(); trackEvent("read_original", { source: article.source_name, article_id: article.id }); }}>
              {t("common.readOriginal")}
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}
